import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AIThinkingOrb } from "./components/motion/AIThinkingOrb";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import BulkGeneratorPage from "./pages/BulkGeneratorPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SettingsPage from "./pages/SettingsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminPayoutPage from "./pages/AdminPayoutPage";
import AdminWaitlistPage from "./pages/AdminWaitlistPage";
import AdminSystemHealthPage from "./pages/AdminSystemHealthPage";
import PhotoReelPage from "./pages/PhotoReelPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import VideoGalleryPage from "./pages/VideoGalleryPage";
import AdminOffersPage from "./pages/AdminOffersPage";
import AdminCouponsPage from "./pages/AdminCouponsPage";
import AdminLiveStatsPage from "./pages/AdminLiveStatsPage";
import AdminMoodboardPage from "./pages/AdminMoodboardPage";
import TokensPage from "./pages/TokensPage";
import TasksPage from "./pages/TasksPage";
import AIVideoGeneratorPage from "./pages/AIVideoGeneratorPage";
import CommunityPage from "./pages/CommunityPage";
import CommunityProfilePage from "./pages/CommunityProfilePage";
import MyCommunityPostsPage from "./pages/MyCommunityPostsPage";
import DesignerStudioPage from "./pages/DesignerStudioPage";
import ArticleStudioPage from "./pages/ArticleStudioPage";
import ExplainerPage from "./pages/ExplainerPage";
import CGIStudioPage from "./pages/CGIStudioPage";
import TimelineEditorPage from "./pages/TimelineEditorPage";
import ComposerPage from "./pages/ComposerPage";
import CreatorProfilePage from "./pages/CreatorProfilePage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import MovieStudioPage from "./pages/MovieStudioPage";

// Components
import OnboardingPopup from "./components/OnboardingPopup";
import CookieConsent from "./components/CookieConsent";
import { OutOfTokensProvider } from "./components/OutOfTokensModal";
import { ModeProvider } from "./context/ModeContext";
import ModePicker from "./components/ModePicker";

// Layout
import DashboardLayout from "./layouts/DashboardLayout";

import "./App.css";

const CgiRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_admin) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-8" data-testid="cgi-unavailable">
        <h2 className="font-display font-black text-3xl text-ink-950 mb-3">CGI is temporarily unavailable</h2>
        <p className="text-ink-500 text-sm max-w-md">We're making fixes to the CGI realm. It'll be back soon — meanwhile, everything in Designing is open.</p>
      </div>
    );
  }
  return children;
};

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <AIThinkingOrb label="Loading" size={80} />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Suppress the per-mode onboarding tour on /admin routes — admins
  // don't need the dashboard walkthrough and the overlay was blocking
  // dialog clicks on first visit. (iter-46 UX note.)
  const onAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  return (
    <>
      {!onAdminRoute && <OnboardingPopup />}
      {children}
    </>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <AIThinkingOrb label="Loading" size={80} />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/creator/:handle" element={<CreatorProfilePage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="bulk" element={<BulkGeneratorPage />} />
        <Route path="ai-video" element={<AIVideoGeneratorPage />} />
        <Route path="photo-reel" element={<PhotoReelPage />} />
        <Route path="community" element={<CommunityPage />} />
        <Route path="community/me" element={<MyCommunityPostsPage />} />
        <Route path="community/u/:userId" element={<CommunityProfilePage />} />
        <Route path="designer" element={<DesignerStudioPage />} />
        <Route path="articles" element={<ArticleStudioPage />} />
        <Route path="explainer" element={<CgiRoute><ExplainerPage /></CgiRoute>} />
        <Route path="movie" element={<CgiRoute><MovieStudioPage /></CgiRoute>} />
        <Route path="cgi" element={<CgiRoute><CGIStudioPage /></CgiRoute>} />
        <Route path="timeline" element={<CgiRoute><TimelineEditorPage /></CgiRoute>} />
        <Route path="composer" element={<CgiRoute><ComposerPage /></CgiRoute>} />
        <Route path="profile" element={<ProfileSettingsPage />} />
        <Route path="gallery" element={<VideoGalleryPage />} />
        <Route path="tokens" element={<TokensPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><DashboardLayout isAdmin /></ProtectedRoute>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="payout" element={<AdminPayoutPage />} />
        <Route path="waitlist" element={<AdminWaitlistPage />} />
        <Route path="system-health" element={<AdminSystemHealthPage />} />
        <Route path="offers" element={<AdminOffersPage />} />
        <Route path="coupons" element={<AdminCouponsPage />} />
        <Route path="live" element={<AdminLiveStatsPage />} />
        <Route path="moodboard" element={<AdminMoodboardPage />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ModeProvider>
            <OutOfTokensProvider>
              <AppRoutes />
              <ModePicker />
              <CookieConsent />
              <Toaster position="bottom-center" richColors closeButton />
            </OutOfTokensProvider>
          </ModeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
